import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { intelligentSearchService } from '../../services/IntelligentSearchService';
import { useAuth } from '../../context/AuthContext';
import ConfirmationModal from '../common/ConfirmationModal';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';

const IntelligentSearchAreas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchAreas, setSearchAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAreas, setSelectedAreas] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'ready', 'completed', 'failed'
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'area_name', 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'default',
    confirmText: 'OK',
    cancelText: 'Cancel'
  });

  useEffect(() => {
    loadSearchAreas();
  }, [currentPage, filter, sortBy, sortOrder]);

  const loadSearchAreas = async () => {
    setLoading(true);
    
    const params = {
      page: currentPage,
      page_size: pageSize,
      status: filter !== 'all' ? filter : undefined,
    };

    const { data, error } = await intelligentSearchService.getIntelligentSearchAreas(params);
    
    if (data) {
      setSearchAreas(data.search_areas || []);
      setTotalCount(data.total || 0);
    } else if (error) {
      console.error('Error loading search areas:', error);
      toast.error('Failed to load search areas');
    }
    
    setLoading(false);
  };

  const showConfirmation = ({ title, message, onConfirm, type = 'default', confirmText = 'OK', cancelText = 'Cancel' }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      type,
      confirmText,
      cancelText
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {},
      type: 'default',
      confirmText: 'OK',
      cancelText: 'Cancel'
    });
  };

  const handleAreaSelection = (areaId, isSelected) => {
    const newSelected = new Set(selectedAreas);
    if (isSelected) {
      newSelected.add(areaId);
    } else {
      newSelected.delete(areaId);
    }
    setSelectedAreas(newSelected);
  };

  const selectAllVisible = () => {
    const allVisibleIds = new Set(searchAreas.map(area => area.id));
    setSelectedAreas(allVisibleIds);
  };

  const deselectAll = () => {
    setSelectedAreas(new Set());
  };

  const handleDeleteSelected = () => {
    if (selectedAreas.size === 0) {
      toast.error('No areas selected');
      return;
    }

    showConfirmation({
      title: 'Delete Selected Areas',
      message: `Are you sure you want to delete ${selectedAreas.size} search area${selectedAreas.size !== 1 ? 's' : ''}? This will also delete all associated circles and search sessions.`,
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        const deletePromises = Array.from(selectedAreas).map(areaId => 
          intelligentSearchService.deleteIntelligentSearchArea(areaId)
        );

        try {
          await Promise.all(deletePromises);
          toast.success(`Deleted ${selectedAreas.size} search area${selectedAreas.size !== 1 ? 's' : ''}`);
          setSelectedAreas(new Set());
          loadSearchAreas();
        } catch (error) {
          console.error('Error deleting areas:', error);
          toast.error('Failed to delete some areas');
          loadSearchAreas(); // Reload to show current state
        }
      }
    });
  };

  const handleRegenerateCircles = async (area) => {
    showConfirmation({
      title: 'Regenerate Circles',
      message: `Regenerate optimized circles for "${area.area_name}"? This will delete existing circles and generate new ones.`,
      type: 'warning',
      confirmText: 'Regenerate',
      onConfirm: async () => {
        const { data, error } = await intelligentSearchService.regenerateCircles(area.id);
        
        if (data) {
          toast.success('Regenerating circles...');
          loadSearchAreas();
        } else if (error) {
          toast.error('Failed to regenerate circles');
          console.error('Error regenerating circles:', error);
        }
      }
    });
  };

  const handleViewArea = (area) => {
    navigate('/intelligent-search', { state: { loadAreaId: area.id } });
  };

  const getStatusBadge = (status) => {
    const color = intelligentSearchService.getStatusColor(status);
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>
        {intelligentSearchService.formatSearchAreaStatus(status)}
      </span>
    );
  };

  const filteredAndSortedAreas = [...searchAreas].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'area_name':
        comparison = a.area_name.localeCompare(b.area_name);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'created_at':
      default:
        comparison = new Date(a.created_at) - new Date(b.created_at);
        break;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && searchAreas.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Intelligent Search Areas</h1>
              <p className="text-sm text-gray-600">Manage your AI-optimized search areas and circles</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/intelligent-search')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Create New Area
              </button>
              <button
                onClick={() => navigate('/intelligent-search/dashboard')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              {/* Filter by Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="ready">Ready</option>
                  <option value="searching">Searching</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Sort Controls */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                <div className="flex space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="created_at">Created Date</option>
                    <option value="area_name">Name</option>
                    <option value="status">Status</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            <div className="flex items-center space-x-3">
              {selectedAreas.size > 0 && (
                <>
                  <span className="text-sm text-gray-600">
                    {selectedAreas.size} selected
                  </span>
                  <button
                    onClick={handleDeleteSelected}
                    className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md hover:bg-red-100 text-sm"
                  >
                    Delete Selected
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 text-sm"
                  >
                    Deselect All
                  </button>
                </>
              )}
              {selectedAreas.size === 0 && filteredAndSortedAreas.length > 0 && (
                <button
                  onClick={selectAllVisible}
                  className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                >
                  Select All Visible
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Areas List */}
        {filteredAndSortedAreas.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' ? 'No Search Areas Found' : `No ${filter} Areas Found`}
            </h3>
            <p className="text-gray-600 mb-4">
              {filter === 'all' 
                ? 'Create your first intelligent search area to get started'
                : `Try changing the filter or create a new search area`
              }
            </p>
            <button
              onClick={() => navigate('/intelligent-search')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Search Area
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-200">
                {filteredAndSortedAreas.map((area) => (
                  <div key={area.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedAreas.has(area.id)}
                          onChange={(e) => handleAreaSelection(area.id, e.target.checked)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">{area.area_name}</h3>
                            {getStatusBadge(area.status)}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="font-medium">Circles:</span> {area.optimized_circles?.length || 0}
                            </div>
                            <div>
                              <span className="font-medium">Business Types:</span> {area.included_types?.length || 0}
                            </div>
                            <div>
                              <span className="font-medium">AI Optimization:</span> {area.use_ai_optimization ? 'Enabled' : 'Disabled'}
                            </div>
                            <div>
                              <span className="font-medium">Created:</span> {new Date(area.created_at).toLocaleDateString()}
                            </div>
                          </div>

                          {area.included_types && area.included_types.length > 0 && (
                           <div className="mb-3">
                             <span className="text-sm font-medium text-gray-700 mr-2">Business Types:</span>
                             <div className="flex flex-wrap gap-1 mt-1">
                               {area.included_types.slice(0, 3).map((type, index) => (
                                 <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                   {type.replace(/_/g, ' ')}
                                 </span>
                               ))}
                               {area.included_types.length > 3 && (
                                 <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                   +{area.included_types.length - 3} more
                                 </span>
                               )}
                             </div>
                           </div>
                         )}

                         {area.optimized_circles && area.optimized_circles.length > 0 && (
                           <div className="text-sm text-gray-600">
                             <span className="font-medium">Circle Stats:</span>
                             <span className="ml-2">
                               Avg Radius: {intelligentSearchService.formatRadius(
                                 area.optimized_circles.reduce((sum, circle) => sum + circle.radius, 0) / area.optimized_circles.length
                               )}
                             </span>
                             <span className="ml-4">
                               High Confidence: {area.optimized_circles.filter(c => (c.confidence_score || 0) >= 0.8).length}
                             </span>
                             <span className="ml-4">
                               Searched: {area.optimized_circles.filter(c => c.has_been_searched).length}
                             </span>
                           </div>
                         )}
                       </div>
                     </div>

                     <div className="flex items-center space-x-2 ml-4">
                       <button
                         onClick={() => handleViewArea(area)}
                         className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                       >
                         View/Edit
                       </button>
                       
                       {area.status === 'ready' && area.optimized_circles?.length > 0 && (
                         <button
                           onClick={() => handleRegenerateCircles(area)}
                           className="px-3 py-2 bg-orange-50 border border-orange-200 text-orange-700 rounded-md hover:bg-orange-100 text-sm"
                         >
                           Regenerate
                         </button>
                       )}
                       
                       <button
                         onClick={() => {
                           showConfirmation({
                             title: 'Delete Search Area',
                             message: `Are you sure you want to delete "${area.area_name}"? This will also delete all associated circles and search sessions.`,
                             type: 'danger',
                             confirmText: 'Delete',
                             onConfirm: async () => {
                               const { data, error } = await intelligentSearchService.deleteIntelligentSearchArea(area.id);
                               
                               if (data) {
                                 toast.success('Search area deleted');
                                 loadSearchAreas();
                               } else if (error) {
                                 toast.error('Failed to delete search area');
                                 console.error('Error deleting area:', error);
                               }
                             }
                           });
                         }}
                         className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md hover:bg-red-100 text-sm"
                       >
                         Delete
                       </button>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           </div>

           {/* Pagination */}
           {totalPages > 1 && (
             <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 mt-6">
               <div className="flex items-center justify-between">
                 <div className="text-sm text-gray-600">
                   Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} areas
                 </div>
                 
                 <div className="flex items-center space-x-2">
                   <button
                     onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                     disabled={currentPage === 1}
                     className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                   >
                     Previous
                   </button>
                   
                   <div className="flex items-center space-x-1">
                     {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                       let pageNum;
                       if (totalPages <= 5) {
                         pageNum = i + 1;
                       } else if (currentPage <= 3) {
                         pageNum = i + 1;
                       } else if (currentPage >= totalPages - 2) {
                         pageNum = totalPages - 4 + i;
                       } else {
                         pageNum = currentPage - 2 + i;
                       }
                       
                       return (
                         <button
                           key={pageNum}
                           onClick={() => setCurrentPage(pageNum)}
                           className={`px-3 py-2 text-sm rounded-md ${
                             currentPage === pageNum
                               ? 'bg-blue-600 text-white'
                               : 'border border-gray-300 hover:bg-gray-50'
                           }`}
                         >
                           {pageNum}
                         </button>
                       );
                     })}
                   </div>
                   
                   <button
                     onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                     disabled={currentPage === totalPages}
                     className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                   >
                     Next
                   </button>
                 </div>
               </div>
             </div>
           )}
         </>
       )}

       {/* Summary Stats */}
       {searchAreas.length > 0 && (
         <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
             <div className="text-center">
               <div className="text-2xl font-bold text-blue-600">
                 {searchAreas.filter(a => a.status === 'ready').length}
               </div>
               <div className="text-sm text-gray-500">Ready to Search</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
             <div className="text-center">
               <div className="text-2xl font-bold text-green-600">
                 {searchAreas.filter(a => a.status === 'completed').length}
               </div>
               <div className="text-sm text-gray-500">Completed</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
             <div className="text-center">
               <div className="text-2xl font-bold text-purple-600">
                 {searchAreas.reduce((sum, area) => sum + (area.optimized_circles?.length || 0), 0)}
               </div>
               <div className="text-sm text-gray-500">Total Circles</div>
             </div>
           </div>
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
             <div className="text-center">
               <div className="text-2xl font-bold text-orange-600">
                 {searchAreas.filter(a => a.use_ai_optimization).length}
               </div>
               <div className="text-sm text-gray-500">AI Optimized</div>
             </div>
           </div>
         </div>
       )}
     </div>
   </div>
 );
};

export default IntelligentSearchAreas;